import { Controller } from 'stimulus';
import { isTurboPreview } from '../javascript/lib/stimulus_turbo';
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, isSupported } from 'firebase/messaging';

const FIREBASE_CONFIG = {
  apiKey: 'AIzaSyDN89o5C5hqiFINELTNlmznW7gNp94tfOs',
  authDomain: 'amba-android-44ec1.firebaseapp.com',
  projectId: 'amba-android-44ec1',
  storageBucket: 'amba-android-44ec1.appspot.com',
  messagingSenderId: '599398082069',
  appId: '1:599398082069:web:7d8354bc8c4339a957501c',
};
const VAPID_KEY =
  'BPg3XRTRNdVkw32RI7oirNXauSGOaHOTCLgCpIjObBs5l0wdBnFXs9SQ2nbCigrIO_x-BnbGwhNikiF4bUMbMsI';

const GET_TOKEN_TIMEOUT_MS = 10000;

const CLASS_HIDDEN = 'd-none';
const CLASS_DISABLED = 'disabled';

/**
 * Handle foreground push notifications & our custom notifications toggle
 */
export default class extends Controller {
  static targets = [
    'radioOff',
    'radioOn',
    'radioToggle',
    'browserPermissionDeniedText',
    'toggleContainer',
    'sendTestLink',
  ];

  static values = { organisationId: Number, skipBrowserChecks: Boolean };

  async connect() {
    if (isTurboPreview()) return;

    if (this.skipBrowserChecksValue) {
      this.toggleContainerTarget.classList.remove(CLASS_HIDDEN);
      return;
    }

    // Check browser features & values are present
    if (!this.shouldConnect) return;

    // Check FCM supports this browser
    const fcmSupported = await isSupported().catch(() => false);
    if (!fcmSupported) return;

    this.addBreadcrumb('Basic check completed');

    await fetch('/firebase-messaging-sw.js', { cache: 'reload' })
      .then(() => {
        this.addBreadcrumb('SW Direct fetch complete');
        return;
      })
      .catch((error) => {
        this.report('Failed to fetch SW file directly', error);
      });

    this.registration = await this.serviceWorkerRegistration();
    if (!this.registration) {
      this.report('No service worker');
      return;
    }

    this.addBreadcrumb('SW registered');

    this.app = initializeApp(FIREBASE_CONFIG);
    this.messaging = getMessaging(this.app);

    this.toggleContainerTarget.classList.remove(CLASS_HIDDEN);

    if (Notification.permission === 'granted') {
      this.addBreadcrumb('Permission granted');

      if (this.hasRadioOffTarget) this.syncTokenAndToggle();
      this.setupNativePermissionsChangeListener();
    } else if (Notification.permission === 'denied') {
      this.addBreadcrumb('Permission denied');

      this.disableToggle();
      this.setupNativePermissionsChangeListener();
    } else {
      this.addBreadcrumb('No permission set');

      this.requestPermission();
    }
  }

  get shouldConnect() {
    return (
      'Notification' in window &&
      'serviceWorker' in navigator &&
      this.hasOrganisationIdValue
    );
  }

  //// Service worker registration
  async serviceWorkerRegistration() {
    try {
      let registration = await navigator.serviceWorker.getRegistration(
        '/firebase-messaging-sw.js'
      );

      if (!registration) {
        registration = await navigator.serviceWorker.register(
          '/firebase-messaging-sw.js',
          { scope: '/' }
        );
      }

      await navigator.serviceWorker.ready;
      return registration;
    } catch (error) {
      this.report('Service worker registration failed', error);
      return null;
    }
  }

  //// Browser permissions

  async requestPermission() {
    this.addBreadcrumb('requestPermission');

    // Safari returns 'denied' from requestPermission() when called without a
    // user gesture, but does not persist the denial — Notification.permission
    // remains 'default'. Chrome throws instead. We ignore the return value and
    // read Notification.permission (the actual browser state) after the call.
    try {
      await Notification.requestPermission();
    } catch (error) {
      this.report('requestPermission error', error);
    }

    if (Notification.permission === 'granted') {
      this.syncTokenAndToggle({ forceUpsert: true });
    } else if (Notification.permission === 'denied') {
      this.disableToggle();
      this.addBreadcrumb('Permission denied');
    } else {
      if (this.hasRadioOffTarget) this.radioOffTarget.checked = true;
      this.addBreadcrumb('Permission not granted');
    }
  }

  setupNativePermissionsChangeListener() {
    if (
      Notification.permission === 'granted' ||
      Notification.permission === 'denied'
    ) {
      navigator.permissions
        .query({ name: 'notifications' })
        .then((notificationPermission) => {
          notificationPermission.onchange = () => {
            this.handleNativePermissionsChange(notificationPermission.state);
          };

          return;
        })
        .catch((error) => {
          this.report('Error handling browser permissions change:', error);
        });
    }
  }

  async handleNativePermissionsChange(state) {
    if (state === 'denied') {
      if (this.currentToken) this.postTokenUnregistration(this.currentToken);
      this.disableToggle();
      if (this.hasSendTestLinkTarget) {
        this.sendTestLinkTarget.classList.add(CLASS_HIDDEN);
      }
    } else if (state === 'granted') {
      this.requestPermission();
    }
  }

  //// Tokens

  async getTokensFromServer() {
    try {
      const response = await fetch(
        `/web_notification_tokens?organisation_id=${this.organisationIdValue}`
      );

      if (!response.ok) {
        this.report(
          `Failed to fetch tokens from server: Status: ${response.status}, Url: ${response.url}`
        );
        return [];
      }

      const data = await response.json();

      if (Array.isArray(data.tokens)) {
        return data.tokens;
      } else {
        this.report(
          `Failed to fetch valid token list: Data: ${JSON.stringify(data)}`
        );
        return [];
      }
    } catch (error) {
      this.report('Error fetching tokens from server:', error);
      return [];
    }
  }

  postToken(token) {
    const data = {
      token: token,
      organisation_id: this.organisationIdValue,
    };

    fetch('/web_notification_tokens', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((response) => {
        if (response.ok) {
          this.currentToken = token;
          this.enableToggle();
          this.toggleRadioOn();
        } else {
          this.report(
            `Failed to post token to server: Status: ${response.status}, Url: ${response.url}`
          );
        }

        return;
      })
      .catch((error) => {
        this.report('Error posting token to server:', error);
      });
  }

  postTokenUnregistration(token) {
    const data = {
      token: token,
    };

    fetch(
      `/web_notification_tokens/${token}?organisation_id=${this.organisationIdValue}`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    )
      .then((response) => {
        if (response.ok) {
          this.currentToken = null;

          return;
        } else {
          this.report(
            `Failed to delete token from server: Status: ${response.status}, Url: ${response.url}`
          );

          return;
        }
      })
      .catch((error) => {
        this.report('Error deleting token from server:', error);
      });
  }

  //// Notifications toggle

  getFCMToken = async () => {
    this.addBreadcrumb('getFCMToken race');

    let timeoutId;

    const timeoutPromise = new Promise((resolve, reject) => {
      timeoutId = setTimeout(
        () => reject(new Error('getToken timeout')),
        GET_TOKEN_TIMEOUT_MS
      );
    });

    try {
      const token = await Promise.race([
        getToken(this.messaging, {
          serviceWorkerRegistration: this.registration,
          vapidKey: VAPID_KEY,
        }),
        timeoutPromise,
      ]);

      return token;
    } catch (error) {
      this.report(error.message || 'getToken failed', error);
      return null;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }
  };

  async syncTokenAndToggle({ forceUpsert = false } = {}) {
    this.addBreadcrumb('Start syncTokenAndToggle');

    if (!this.registration) {
      this.registration = await this.serviceWorkerRegistration();
    }

    if (!this.registration) {
      this.report('No service worker');
      return;
    }

    this.addBreadcrumb('syncTokenAndToggle has SW registration');

    const currentToken = await this.getFCMToken();

    if (!currentToken) {
      this.report('getToken returned null');

      return;
    }

    this.addBreadcrumb('syncTokenAndToggle has token');

    this.currentToken = currentToken;

    // Check server state
    const userTokens = await this.getTokensFromServer();

    // Clean up this browser's previous token if it changed (FCM token refresh)
    // Only delete the token we previously stored, not tokens from other browsers
    // In incognito/private mode, localStorage may be unavailable or ephemeral
    this.cleanupPreviousToken(currentToken, userTokens);

    if (Notification.permission === 'granted') {
      if (userTokens.includes(currentToken)) {
        this.toggleRadioOn();
      } else if (forceUpsert) {
        this.postToken(currentToken);
      }
    }
  }

  async handleNotificationToggleChange() {
    if (this.radioOnTarget.checked === true) {
      if (this.hasSendTestLinkTarget) {
        this.sendTestLinkTarget.classList.remove(CLASS_HIDDEN);
      }
      if (!this.skipBrowserChecksValue) this.requestPermission();
    } else {
      if (this.hasSendTestLinkTarget) {
        this.sendTestLinkTarget.classList.add(CLASS_HIDDEN);
      }

      if (this.skipBrowserChecksValue) return;

      const userTokens = await this.getTokensFromServer();

      if (this.currentToken && userTokens.includes(this.currentToken)) {
        this.postTokenUnregistration(this.currentToken);
      }
    }
  }

  toggleRadioOn() {
    if (this.hasRadioOnTarget) {
      this.radioOnTarget.checked = true;
      this.browserPermissionDeniedTextTarget.classList.add(CLASS_HIDDEN);
      if (this.hasSendTestLinkTarget) {
        this.sendTestLinkTarget.classList.remove(CLASS_HIDDEN);
      }
    }
  }

  enableToggle() {
    if (this.hasRadioOnTarget) {
      this.browserPermissionDeniedTextTarget.classList.add(CLASS_HIDDEN);
      this.radioToggleTarget.classList.remove(CLASS_DISABLED);
      [this.radioOnTarget, this.radioOffTarget].forEach((input) => {
        input.disabled = false;
      });
    }
  }

  disableToggle() {
    if (this.hasRadioOnTarget) {
      this.browserPermissionDeniedTextTarget.classList.remove(CLASS_HIDDEN);
      this.radioToggleTarget.classList.add(CLASS_DISABLED);
      [this.radioOnTarget, this.radioOffTarget].forEach((input) => {
        input.disabled = true;
      });
    }
  }

  report(message = null, error = null) {
    console.error(message, error);
    if (!window.appsignal) return;

    if (error && message) {
      window.appsignal.sendError(error, (span) => {
        span.setTags({ context: message });
      });
    } else if (message) {
      window.appsignal.sendError(new Error(message));
    } else {
      window.appsignal.sendError(error);
    }
  }

  addBreadcrumb(message) {
    if (!window.appsignal) return;

    window?.appsignal?.addBreadcrumb({
      category: 'Push notifications',
      action: message,
    });
  }

  cleanupPreviousToken(currentToken, userTokens) {
    try {
      const previousToken = localStorage.getItem('fcm_token');

      if (
        previousToken &&
        previousToken !== currentToken &&
        userTokens.includes(previousToken)
      ) {
        this.postTokenUnregistration(previousToken);
      }

      localStorage.setItem('fcm_token', currentToken);
    } catch {
      // localStorage unavailable (storage quota exceeded, etc.)
      // Skip cleanup - stale tokens will be handled by backend cleanup on 404
    }
  }
}
